import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function SubmitIdeaDialog({ onCreated }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", tags: "" });

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/ideas", {
        title: form.title,
        description: form.description,
        submitter: user.name,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Idea submitted — AI is scoring the early gates");
      setForm({ title: "", description: "", tags: "" });
      setOpen(false);
      onCreated?.(res.data);
    } catch (e) {
      toast.error("Failed to submit idea");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="submit-idea-btn" className="rounded-sm font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> Submit Idea
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight">
            Submit a new idea
          </DialogTitle>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-ai" />
            AI will auto-score relevance & feasibility on the early gates.
          </p>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Title</Label>
            <Input
              data-testid="idea-title-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Autonomous SOC Triage Agent"
              className="mt-1.5 rounded-sm bg-background"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
            <Textarea
              data-testid="idea-desc-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What problem does it solve, for whom, and why now?"
              rows={4}
              className="mt-1.5 rounded-sm bg-background resize-none"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tags (comma separated)</Label>
            <Input
              data-testid="idea-tags-input"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="security, agentic, automation"
              className="mt-1.5 rounded-sm bg-background"
            />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="idea-submit-confirm" onClick={submit} disabled={loading} className="rounded-sm font-semibold">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {loading ? "Scoring…" : "Submit & Score"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
